import { useMemo } from 'react';
import { isNumber, isString, toNumber } from 'lodash-es';
import { formatEther } from 'viem';
import { useSession } from '../contexts';
import {
  useUserBalanceAPIQuery,
  useUserBalanceBlockchainQuery,
} from '../services/queries';
import { useIsBlockchainVersion } from './useIsBlockchainVersion.ts';

const toOptionalNumber = (value: unknown): number | undefined => {
  if (isNumber(value)) {
    return value;
  }
  if (isString(value) && value !== '') {
    return toNumber(value);
  }
  return undefined;
};

export const useUserBalance = () => {
  const { isAuthenticated } = useSession();
  const blockchainVersion = useIsBlockchainVersion();

  const { data: apiBalanceData, ...restAPIBalanceResponse } =
    useUserBalanceAPIQuery({
      enabled: isAuthenticated && !blockchainVersion,
    });

  const { data: blockchainData, ...restBlockchainBalanceResponse } =
    useUserBalanceBlockchainQuery({
      enabled: blockchainVersion,
    });

  return useMemo(() => {
    if (blockchainVersion) {
      return {
        ...restBlockchainBalanceResponse,
        data: blockchainData?.value
          ? parseFloat(formatEther(blockchainData.value))
          : 0,
        usd: undefined,
        currency: undefined,
        amount: undefined,
        exchangeRate: undefined,
        bettingBalance: blockchainData?.value
          ? parseFloat(formatEther(blockchainData.value))
          : 0,
      };
    }

    const payload = apiBalanceData?.data;
    const data = isNumber(payload?.balance)
      ? payload.balance
      : isString(payload?.balance)
        ? toNumber(payload.balance)
        : undefined;

    return {
      ...restAPIBalanceResponse,
      data,
      usd: toOptionalNumber(payload?.usd ?? undefined),
      currency: payload?.currency ?? undefined,
      amount: toOptionalNumber(payload?.amount ?? undefined),
      exchangeRate: toOptionalNumber(payload?.exchange_rate ?? undefined),
      bettingBalance: toOptionalNumber(payload?.usd ?? undefined) ?? data,
    };
  }, [
    blockchainVersion,
    restAPIBalanceResponse,
    restBlockchainBalanceResponse,
    apiBalanceData,
    blockchainData,
  ]);
};
